import React, { useState } from "react";
import {
  UploadPageWrapper,
  Navigation,
  NavigationWrapper,
  Next,
  Previous,
  NavLeft,
  PageTitle,
} from "./styles";
import FileUpload from "../../components/upload/FileUpload";
import CsvDataPreview from "../../components/data/CsvDataPreview";
import { Button } from "../../components/common/button";
import { useTranslation } from "react-i18next";
import type { CsvUploadResponse } from "../../types/Csvtypes";
import api from "../../network/api";

type Datum = {
  file: File;
  data: { summary?: CsvUploadResponse["data"]; error?: string } | undefined;
  isLoading: boolean;
};

const UploadCSV: React.FC = () => {
  const [data, setData] = useState<Datum[]>([]);
  const [selectedFile, setSelectedFile] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>();
  const { t } = useTranslation();

  /* ---------- choose files ---------- */
  const handleFilesChosen = (files: File[]) => {
    const startIndex = data.length;
    const newEntries: Datum[] = files.map((file) => ({
      file,
      data: undefined,
      isLoading: false,
    }));
    setData((prev) => [...prev, ...newEntries]);
    setSelectedFile(startIndex);
  };

  /* ---------- upload to backend ---------- */
  const startUpload = async () => {
    if (selectedFile === null) return;
    setData((prev) => {
      const copy = [...prev];
      copy[selectedFile].isLoading = true;
      return copy;
    });

    const res = await api.processCSVFile(data[selectedFile].file, (e) =>
      setUploadProgress(
        e.total ? Math.round((100 * e.loaded) / e.total) : undefined
      )
    );

    setData((prev) => {
      const copy = [...prev];
      copy[selectedFile].isLoading = false;
      if (res && res.success) {
        copy[selectedFile].data = { summary: res.data };
      } else if (res && res.data?.errors?.includes("RATE_LIMIT")) {
        copy[selectedFile].data = { error: "RATE_LIMIT" };
      } else {
        copy[selectedFile].data = { error: "BACKEND_ERROR" };
      }
      return copy;
    });
  };

  /* ---------- render ---------- */
  return (
    <UploadPageWrapper>
      {selectedFile === null && <PageTitle>{t("UPLOAD_CSV_PAGE")}</PageTitle>}

      {/* Nav bar */}
      {selectedFile !== null && (
        <Navigation>
          <NavLeft>
            <Button
              narrow
              label={t("ADD_NEW_FILES")}
              onClick={() => setSelectedFile(null)}
            />
          </NavLeft>

          <NavigationWrapper>
            <Next
              disabled={selectedFile === data.length - 1}
              onClick={
                selectedFile === data.length - 1
                  ? undefined
                  : () => setSelectedFile(selectedFile + 1)
              }
            >
              {t("NEXT")}
            </Next>
            <span>
              {t("FILE_OF_FILES", {
                index: selectedFile + 1,
                count: data.length,
              })}
            </span>
            <Previous
              disabled={selectedFile === 0}
              onClick={
                selectedFile === 0
                  ? undefined
                  : () => setSelectedFile(selectedFile - 1)
              }
            >
              {t("PREVIOUS")}
            </Previous>
          </NavigationWrapper>
        </Navigation>
      )}

      {/* Main area */}
      {selectedFile === null ? (
        <FileUpload
          data={data}
          onStartClicked={handleFilesChosen}
          uploadProgress={uploadProgress}
          onCancel={() => setSelectedFile(0)}
          allowedFileTypes={[
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
            "text/csv",
          ]}
        />
      ) : (
        <CsvDataPreview
          fileName={data[selectedFile].file.name}
          summary={data[selectedFile].data?.summary}
          loading={data[selectedFile].isLoading}
          fileLoadError={data[selectedFile].data?.error}
          onStartUpload={startUpload}
        />
      )}
    </UploadPageWrapper>
  );
};

export default UploadCSV;
